import { ReactElement } from 'react'
import { useWatch } from 'react-hook-form'
import type { ProjectConnectionUsageDTO } from '@api/fastAPI.schemas'
import { UseMutationResult } from '@tanstack/react-query'
import { useSnackbar } from '@ui/snackbar'
import { ConnectionList } from 'src/components/manager/connections'

interface Props {
  connections: Array<Connection | ProjectConnection>
  onDeleteConnection: (connectionId: string) => void
  /** How many anchors use each Zugverbinder, by connection guid. */
  usageByGuid: Record<string, ProjectConnectionUsageDTO>
  editMutation:
    | UseMutationResult<Connection, unknown, Connection, unknown>
    | UseMutationResult<ProjectConnection, unknown, ProjectConnection, unknown>
  createMutation:
    | UseMutationResult<Connection, unknown, Connection, unknown>
    | UseMutationResult<ProjectConnection, unknown, ProjectConnection, unknown>
  defaultRowsPerPage?: 5 | 10 | 25
}

const ConnectionListWithDeleteCheck = ({
  connections,
  onDeleteConnection,
  usageByGuid,
  editMutation,
  createMutation,
  defaultRowsPerPage,
}: Props): ReactElement => {
  const { enqueueSnackbar } = useSnackbar()
  const innerInner = useWatch({ name: 'inner_inner' }) as string[]
  const innerFoundation = useWatch({ name: 'inner_foundation' }) as string[]
  const outerOuter = useWatch({ name: 'outer_outer' }) as string[]
  const outerFoundation = useWatch({ name: 'outer_foundation' }) as string[]

  const handleDeleteConnection = (connection: Connection | ProjectConnection) => {
    const connectionGuid = connection.guid

    // Check if this connection is the only one in any prioritization
    const prioritizations = [
      { name: 'Innen-Innen', ids: innerInner },
      { name: 'Innen-Fundament', ids: innerFoundation },
      { name: 'Außen-Außen', ids: outerOuter },
      { name: 'Außen-Fundament', ids: outerFoundation },
    ]

    const blockingPrioritizations = prioritizations.filter(
      p => p.ids?.length === 1 && p.ids[0] === connectionGuid,
    )

    if (blockingPrioritizations.length > 0) {
      const prioritizationNames = blockingPrioritizations.map(p => p.name).join(', ')
      enqueueSnackbar(
        `Diese Zugverbindung kann nicht gelöscht werden, da sie die einzige in der Priorisierung ${prioritizationNames} ist.`,
        { variant: 'error' },
      )
      return
    }

    onDeleteConnection(connectionGuid)
  }

  return (
    <ConnectionList
      connections={connections}
      onDelete={handleDeleteConnection}
      usageByGuid={usageByGuid}
      editMutation={editMutation}
      createMutation={createMutation}
      defaultRowsPerPage={defaultRowsPerPage}
      isProjectLibrary
    />
  )
}

export default ConnectionListWithDeleteCheck
